import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useConfig } from '../../context/ConfigContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import UserDropdown from '../ui/UserDropdown';
import SearchBar from '../ui/SearchBar';

const { FiSun, FiMoon, FiUser, FiMenu, FiX } = FiIcons;

const Navbar = () => {
    const { user, isAuthenticated } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { config } = useConfig();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navItems = [
        { name: 'Quran', path: '/quran' },
        { name: 'Hadith', path: '/hadith' },
        { name: 'Duas', path: '/duas' },
        { name: 'Books', path: '/books' },
        { name: 'Contact', path: '/contact' }
    ];

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-emerald-200 dark:border-gray-700">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center space-x-3">
                        {config.platformLogo ? (
                            <img
                                src={`${import.meta.env.VITE_API_URL}${config.platformLogo}`}
                                alt={config.platformName}
                                className="w-10 h-10 rounded-lg object-cover"
                            />
                        ) : (
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                            >
                                ﷽
                            </motion.div>
                        )}

                        <span className="text-xl font-bold text-emerald-800 dark:text-emerald-400">
                            {config.platformName}
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8 ml-10">
                        {navItems.map((item) => (
                            <Link
                                key={item.name}
                                to={item.path}
                                className="text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 font-medium transition-colors duration-200"
                            >
                                {item.name}
                            </Link>
                        ))}
                    </div>

                    {/* Search Bar */}
                    <div className="hidden md:block flex-1 max-w-md mx-8">
                        <SearchBar />
                    </div>

                    {/* Right Side Actions */}
                    <div className="flex items-center space-x-4">
                        {/* Theme Toggle */}
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={toggleTheme}
                            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                        >
                            <SafeIcon
                                icon={theme === 'light' ? FiMoon : FiSun}
                                className="w-5 h-5"
                            />
                        </motion.button>

                        {/* User Authentication */}
                        {isAuthenticated ? (
                            <UserDropdown user={user} />
                        ) : (
                            <Link
                                to="/signin"
                                className="hidden md:flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors duration-200"
                            >
                                <SafeIcon icon={FiUser} className="w-4 h-4" />
                                <span>Sign In</span>
                            </Link>
                        )}

                        {/* Mobile Menu Toggle */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="md:hidden p-2 rounded-lg text-gray-600 dark:text-gray-400"
                        >
                            <SafeIcon
                                icon={isMobileMenuOpen ? FiX : FiMenu}
                                className="w-6 h-6"
                            />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden bg-white dark:bg-gray-900 border-t border-emerald-200 dark:border-gray-700"
                    >
                        <div className="px-4 py-4 space-y-4">
                            {/* Mobile Search */}
                            <SearchBar />

                            {/* Mobile Navigation */}
                            {navItems.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.path}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="block py-2 text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 font-medium"
                                >
                                    {item.name}
                                </Link>
                            ))}

                            {/* Mobile Sign In */}
                            {!isAuthenticated && (
                                <Link
                                    to="/signin"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg w-full justify-center"
                                >
                                    <SafeIcon icon={FiUser} className="w-4 h-4" />
                                    <span>Sign In</span>
                                </Link>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;